import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

export type CallbackFunction = (response: AxiosResponse | Error) => void;

/**
 * Build a request configuration containing an optional Bearer token.
 *
 * @param token Authorization token to send in the `Authorization` header.
 * @returns Axios request configuration with default headers.
 */
const getDefaultConfig = (token: string): AxiosRequestConfig => ({
  headers: {
    'Authorization': token ? `Bearer ${token}` : '',
  }
});

/**
 * Helper for executing POST requests.
 *
 * @param client Axios instance used to perform the request.
 * @param url Target endpoint.
 * @param data Payload to send.
 * @param callback Function called with the Axios response or the error.
 * @param token Optional bearer token.
 * @param additionalConfig Extra Axios configuration.
 */
export const basicPost = async (
  client: AxiosInstance,
  url: string,
  data: any,
  callback: CallbackFunction,
  token = '',
  additionalConfig: AxiosRequestConfig = {}
) => {
  try {
    const config = { ...getDefaultConfig(token), ...additionalConfig };
    const response = await client.post(url, data, config);
    callback(response);
  } catch (error) {
    callback(error as Error);
  }
};

/**
 * Helper for executing GET requests.
 *
 * @param client Axios instance used to perform the request.
 * @param url Target endpoint.
 * @param callback Function called with the Axios response or the error.
 * @param token Optional bearer token.
 * @param additionalConfig Extra Axios configuration.
 */
export const basicGet = async (
  client: AxiosInstance,
  url: string,
  callback: CallbackFunction,
  token = '',
  additionalConfig: AxiosRequestConfig = {}
) => {
  try {
    const config = { ...getDefaultConfig(token), ...additionalConfig };
    const response = await client.get(url, config);
    callback(response);
  } catch (error) {
    callback(error as Error);
  }
};

/**
 * Helper for executing PUT requests.
 *
 * @param client Axios instance used to perform the request.
 * @param url Target endpoint.
 * @param data Payload to send.
 * @param callback Function called with the Axios response or the error.
 * @param token Optional bearer token.
 * @param additionalConfig Extra Axios configuration.
 */
export const basicPut = async (
  client: AxiosInstance,
  url: string,
  data: any,
  callback: CallbackFunction,
  token = '',
  additionalConfig: AxiosRequestConfig = {}
) => {
  try {
    const config = { ...getDefaultConfig(token), ...additionalConfig };
    const response = await client.put(url, data, config);
    callback(response);
  } catch (error) {
    callback(error as Error);
  }
};

/**
 * Helper for executing DELETE requests.
 *
 * @param client Axios instance used to perform the request.
 * @param url Target endpoint.
 * @param callback Function called with the Axios response or the error.
 * @param token Optional bearer token.
 * @param additionalConfig Extra Axios configuration.
 */
export const basicDelete = async (
  client: AxiosInstance,
  url: string,
  callback: CallbackFunction,
  token = '',
  additionalConfig: AxiosRequestConfig = {}
) => {
  try {
    const config = { ...getDefaultConfig(token), ...additionalConfig };
    const response = await client.delete(url, config);
    callback(response);
  } catch (error) {
    callback(error as Error);
  }
};
